import { fmtRecent } from '@remote-medium/shared';
import {
  Badge,
  Button,
  Input,
  SearchableSelect,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import type { InferResponseType } from 'hono/client';
import { useState } from 'react';

import { admin } from '../../../lib/client.ts';
import { DataTable, EmptyRow, Notice, StatusRow, tableReady } from '../../../lib/ui.tsx';
import { RejectInUse } from './RejectInUse.tsx';

type Pending = InferResponseType<
  (typeof admin)['role-types']['pending']['$get'],
  200
>['data'][number];
type Taxonomy = InferResponseType<(typeof admin.taxonomy)['$get'], 200>['data'];

// Role types companies and candidates added themselves (Q77). They are in use
// already: keep one, move its users to an existing type, or reject it (its
// author must then pick another). Rejecting one in use asks for a replacement
// first. Every review emails everyone using it (author, candidates, company
// users), so the note is written for all of them.
export function PendingRoleTypes({ onReviewed }: { onReviewed: () => void }) {
  const { data, error, loading, reload } = useApi(
    () => unwrap(admin['role-types'].pending.$get()),
    [],
  );
  const { data: taxonomy, reload: reloadTaxonomy } = useApi(
    () => unwrap(admin.taxonomy.$get()),
    [],
  );
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const ready = tableReady({ data, loading, error });
  const rows = ready ? (data ?? []) : [];
  const approved = (taxonomy?.roleTypes ?? []).filter((r) => r.status === 'approved');
  const industryName = (id: string) =>
    taxonomy?.industries.find((i) => i.id === id)?.name ?? 'Unknown industry';

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setMsg(null);
    try {
      await fn();
      setMsg({ tone: 'info', text: ok });
      reload();
      reloadTaxonomy();
      onReviewed();
    } catch (e) {
      reloadTaxonomy();
      setMsg({ tone: 'error', text: errorMessage(e) });
    }
  };

  return (
    <>
      <p className="m-0 max-w-[65ch] text-sm text-muted-foreground">
        Added by companies and candidates when nothing fitted, and already in use by them. Keep the
        real ones, move synonyms to an existing type, and reject the rest. Every review emails
        everyone using it, not just the person who added it.
      </p>
      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
      <DataTable minWidth="min-w-3xl">
        <thead>
          <tr>
            <th>Role type</th>
            <th>Added by</th>
            <th>In use</th>
            <th>Review</th>
          </tr>
        </thead>
        <tbody>
          <StatusRow colSpan={4} loading={loading} error={error} />
          {ready && rows.length === 0 && (
            <EmptyRow colSpan={4}>Nothing to review. New role types show up here.</EmptyRow>
          )}
          {rows.map((rt) => (
            <tr key={rt.id} className="align-top hover:bg-muted/50">
              <td>
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">{rt.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {rt.family}
                    {rt.industries.length > 0 && ` · ${rt.industries.map(industryName).join(', ')}`}
                  </span>
                  {rt.suggestionNote && (
                    <span className="text-sm text-muted-foreground">“{rt.suggestionNote}”</span>
                  )}
                </div>
              </td>
              <td>
                <div className="flex flex-col gap-1 text-sm">
                  {rt.suggestedBy?.email ? (
                    <>
                      <Badge variant="outline" className="w-fit">
                        {rt.suggestedBy.role === 'company_user' ? 'Company' : 'Candidate'}
                      </Badge>
                      <span className="break-all">{rt.suggestedBy.email}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">Account deleted</span>
                  )}
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {fmtRecent(rt.createdAt)}
                  </span>
                </div>
              </td>
              <td className="text-sm whitespace-nowrap tabular-nums">
                {rt.roleCount} {rt.roleCount === 1 ? 'role' : 'roles'}
                <br />
                {rt.candidateCount} {rt.candidateCount === 1 ? 'candidate' : 'candidates'}
              </td>
              <td>
                <Review
                  roleType={rt}
                  approved={approved}
                  onKeep={(edits) =>
                    act(
                      () =>
                        unwrap(
                          admin['role-types'][':id'].approve.$post({
                            param: { id: rt.id },
                            json: edits,
                          }),
                        ),
                      `Kept “${edits.name}” in ${edits.family}.`,
                    )
                  }
                  onReplace={(intoId, note) =>
                    act(
                      () =>
                        unwrap(
                          admin['role-types'][':id'].replace.$post({
                            param: { id: rt.id },
                            json: { intoId, note },
                          }),
                        ),
                      `Moved “${rt.name}” to ${approved.find((a) => a.id === intoId)?.name}.`,
                    )
                  }
                  onAddAndReplace={(added, note) =>
                    act(async () => {
                      const created = await unwrap(
                        admin.taxonomy['role-types'].$post({
                          json: { ...added, industries: rt.industries },
                        }),
                      );
                      await unwrap(
                        admin['role-types'][':id'].replace.$post({
                          param: { id: rt.id },
                          json: { intoId: created.id, note },
                        }),
                      );
                    }, `Added “${added.name}” and moved “${rt.name}” to it.`)
                  }
                  onReject={(note) =>
                    act(
                      () =>
                        unwrap(
                          admin['role-types'][':id'].reject.$post({
                            param: { id: rt.id },
                            json: { note },
                          }),
                        ),
                      `Rejected “${rt.name}”.`,
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </>
  );
}

function Review({
  roleType,
  approved,
  onKeep,
  onReplace,
  onAddAndReplace,
  onReject,
}: {
  roleType: Pending;
  approved: Taxonomy['roleTypes'];
  onKeep: (edits: { name: string; family: string }) => Promise<void>;
  onReplace: (intoId: string, note: string | null) => Promise<void>;
  onAddAndReplace: (added: { name: string; family: string }, note: string | null) => Promise<void>;
  onReject: (note: string | null) => Promise<void>;
}) {
  const [name, setName] = useState(roleType.name);
  const [family, setFamily] = useState<string | null>(roleType.family);
  const [confirming, setConfirming] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFamily, setNewFamily] = useState<string | null>(roleType.family);
  const inUse = roleType.roleCount + roleType.candidateCount > 0;
  const [intoId, setIntoId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // One action at a time: a second click would hit an already-reviewed row.
  const once = (fn: () => Promise<void>) => {
    setBusy(true);
    void fn().finally(() => setBusy(false));
  };
  const families = [...new Set(approved.map((r) => r.family))].sort((a, b) => a.localeCompare(b));
  const stem = roleType.name.toLowerCase().slice(0, 4);
  const similar = new Set(
    approved.filter((r) => r.name.toLowerCase().includes(stem)).map((r) => r.id),
  );
  const options = [
    ...approved
      .filter((r) => similar.has(r.id))
      .map((r) => ({ value: r.id, label: r.name, group: 'Similar' })),
    ...approved
      .filter((r) => !similar.has(r.id))
      .map((r) => ({ value: r.id, label: r.name, group: r.family })),
  ];
  const trimmedNote = note.trim() || null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={`Name for ${roleType.name}`}
          className="w-44"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="w-40">
          <SearchableSelect
            aria-label={`Family for ${roleType.name}`}
            options={families.map((f) => ({ value: f, label: f }))}
            value={family}
            onChange={setFamily}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !name.trim() || !family}
          onClick={() => family && once(() => onKeep({ name: name.trim(), family }))}
        >
          Keep
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-56">
          <SearchableSelect
            aria-label={`Replace ${roleType.name} with an existing role type`}
            placeholder="Replace with existing…"
            options={options}
            value={intoId}
            onChange={setIntoId}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !intoId}
          onClick={() => intoId && once(() => onReplace(intoId, trimmedNote))}
        >
          Replace
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => (inUse ? setConfirming(true) : once(() => onReject(trimmedNote)))}
        >
          Reject
        </Button>
      </div>
      {confirming && (
        <RejectInUse
          name={roleType.name}
          roleCount={roleType.roleCount}
          candidateCount={roleType.candidateCount}
          consequence="Rejecting leaves them on it, but none of them can be saved again until they pick another type."
          busy={busy}
          canAdd={newName.trim().length > 0 && !!newFamily}
          addFields={
            <>
              <Input
                aria-label={`New role type to replace ${roleType.name}`}
                placeholder="New role type name"
                className="w-44"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <div className="w-40">
                <SearchableSelect
                  aria-label="Family for the new role type"
                  options={families.map((f) => ({ value: f, label: f }))}
                  value={newFamily}
                  onChange={setNewFamily}
                />
              </div>
            </>
          }
          onAdd={() =>
            newFamily &&
            once(() => onAddAndReplace({ name: newName.trim(), family: newFamily }, trimmedNote))
          }
          onRejectAnyway={() => once(() => onReject(trimmedNote))}
          onCancel={() => setConfirming(false)}
        />
      )}
      <Input
        aria-label={`Note to everyone using ${roleType.name}`}
        placeholder="Note to everyone using it (optional)"
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </div>
  );
}
